import { useState } from 'react';
import { AlertCircle, CheckCircle2, Plus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAgeRecommendations } from '@/hooks/useAgeRecommendations';
import { PROTEIN_ADDITIONS, type ProteinAddition } from '@/utils/ageRecommendations';
import { useTelegram } from '@/hooks/useTelegram';
import { supabase } from '@/lib/supabase';
import { enrichMealNutrients } from '@/hooks/useMeals';

interface Props {
  mealId: string;
  totalProteinG: number;
  totalCaloriesKcal: number;
  onAdded?: () => void;
}

export function MealHintBlock({ mealId, totalProteinG, totalCaloriesKcal, onAdded }: Props) {
  const { getMealHint } = useAgeRecommendations();
  const { haptic } = useTelegram();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);

  const hint = getMealHint(totalProteinG, totalCaloriesKcal);
  if (!hint) return null;

  const isWarning = hint.type === 'warning';

  async function addProductToMeal(p: ProteinAddition) {
    setAdding(p.name);
    haptic('medium');
    try {
      // Найдём текущую позицию
      const { data: existingItems } = await supabase
        .from('meal_items')
        .select('position')
        .eq('meal_id', mealId)
        .order('position', { ascending: false })
        .limit(1);
      
      const nextPos = (existingItems?.[0]?.position ?? -1) + 1;

      // Добавляем продукт
      const { error: insertErr } = await supabase.from('meal_items').insert({
        meal_id: mealId,
        name: p.name,
        weight_g: p.weight_g,
        calories: p.calories,
        protein_g: p.protein_g,
        fat_g: p.fat_g,
        carbs_g: p.carbs_g,
        position: nextPos,
      });

      if (insertErr) throw insertErr;

      // Пересчитываем итоги приёма (получаем сумму всех items)
      const { data: allItems } = await supabase
        .from('meal_items')
        .select('calories, protein_g, fat_g, carbs_g')
        .eq('meal_id', mealId);

      if (allItems) {
        const totals = allItems.reduce(
          (acc, it) => ({
            cal: acc.cal + (Number(it.calories) || 0),
            p: acc.p + (Number(it.protein_g) || 0),
            f: acc.f + (Number(it.fat_g) || 0),
            c: acc.c + (Number(it.carbs_g) || 0),
          }),
          { cal: 0, p: 0, f: 0, c: 0 }
        );

        await supabase
          .from('meals')
          .update({
            total_calories: Math.round(totals.cal),
            total_protein_g: totals.p,
            total_fat_g: totals.f,
            total_carbs_g: totals.c,
            nutrients: null,
            nutrients_calculated_at: null,
          })
          .eq('id', mealId);
      }

      // Продукт лёг в приём напрямую, минуя createMeal — микронутриенты
      // у него пустые, дозапрашиваем в фоне (уже заполненные не трогаются)
      enrichMealNutrients(mealId).catch((e) =>
        console.error('[MealHintBlock] enrich failed:', e),
      );

      haptic('success');
      setCatalogOpen(false);
      if (onAdded) onAdded();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось добавить');
    } finally {
      setAdding(null);
    }
  }

  return (
    <div className={`mt-2 rounded-lg p-3 border ${
      isWarning ? 'bg-warn/5 border-warn/30' : 'bg-emerald-glow/5 border-emerald-glow/30'
    }`}>
      <div className="flex items-start gap-2">
        {isWarning ? (
          <AlertCircle size={14} className="text-warn shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 size={14} className="text-emerald-glow shrink-0 mt-0.5" />
        )}
        <p className="text-[11px] leading-relaxed flex-1">{hint.text}</p>
      </div>

      {hint.cta === 'add_protein' && !catalogOpen && (
        <button
          onClick={() => setCatalogOpen(true)}
          className="w-full mt-2 py-1.5 rounded-md bg-emerald-glow/10 text-emerald-glow text-[11px] font-medium hover:bg-emerald-glow/20 transition-colors flex items-center justify-center gap-1"
        >
          <Plus size={11} /> Что добавить?
        </button>
      )}

      <AnimatePresence>
        {catalogOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-line/40 space-y-1.5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-ink-dim font-medium">ДОБАВИТЬ БЕЛОК В ЭТОТ ПРИЁМ</span>
                <button onClick={() => setCatalogOpen(false)} className="text-ink-dim">
                  <X size={12} />
                </button>
              </div>
              {PROTEIN_ADDITIONS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => addProductToMeal(p)}
                  disabled={adding === p.name}
                  className="w-full flex items-center gap-2 p-2 rounded-md bg-bg-elevated hover:bg-emerald-glow/10 transition-colors text-left disabled:opacity-50"
                >
                  <span className="text-base">{p.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{p.name}</div>
                    <div className="text-[10px] text-ink-dim">
                      {p.weight_g}г · +{p.protein_g}г белка · {p.calories} ккал
                    </div>
                  </div>
                  {adding === p.name ? (
                    <div className="w-3 h-3 border-2 border-emerald-glow border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus size={12} className="text-emerald-glow shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
